import { useCallback, useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { FeatureSwitchesPanel } from './R2Admin';
import { AdminFrame } from './AdminFrame';
import { AuditList } from './AuditList';
import {
  Approval,
  ArchiveRow,
  download,
  errorText,
  fmtBytes,
  fmtDate,
  fmtDateTime,
  PlatformSettings,
  rpc,
  SchoolRow,
  SupportStatus,
} from './api';
import { EnterSupportDialog } from './bits';

// ── Approvals (spec E3, F3) ────────────────────────────────────────────────
// Requests that reach Zencera: widening a role in a school with no active
// primary head (approve only after confirming with the school's signatory
// outside the app), and erasing a primary head or school admin (needs a
// SECOND platform admin — the requester can't approve their own).

export function AdminApprovals() {
  const { t } = useI18n();
  return (
    <AdminFrame section="approvals" title={t['pa.navApprovals']}>
      {({ refreshSupport, support }) => <ApprovalsBody onChange={refreshSupport} support={support} />}
    </AdminFrame>
  );
}

function ApprovalsBody({ onChange, support }: { onChange: () => Promise<void>; support: SupportStatus }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [rows, setRows] = useState<Approval[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decide, setDecide] = useState<{ a: Approval; approve: boolean } | null>(null);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState('');
  const [busy, setBusy] = useState(false);
  const [dError, setDError] = useState<string | null>(null);
  // Deciding acts on a person in the school, so it happens in support mode
  // for that school (0010). If not in it yet, enter it first, then decide.
  const [enterFor, setEnterFor] = useState<{ a: Approval; approve: boolean } | null>(null);
  const inSchool = (a: Approval) => support.active && support.school_id === a.school_id;
  function start(a: Approval, approve: boolean) {
    setNote('');
    setConfirmed('');
    setDError(null);
    if (inSchool(a)) setDecide({ a, approve });
    else setEnterFor({ a, approve });
  }

  const load = useCallback(async () => {
    const r = await rpc<Approval[]>('platform_list_approvals');
    if (r.error) setError(errorText(t, r.error));
    else setRows(r.data);
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  async function submit() {
    if (!decide) return;
    setBusy(true);
    setDError(null);
    const r = await rpc('platform_decide_approval', {
      p_approval_id: decide.a.id,
      p_approve: decide.approve,
      p_note: note || null,
      p_confirmed_with: confirmed || null,
    });
    setBusy(false);
    if (r.error) return setDError(errorText(t, r.error));
    announce(decide.approve ? t['pa.approved'] : t['pa.rejected']);
    setDecide(null);
    await load();
    await onChange();
  }

  const roleName = (a: Approval) => (a.payload.role ? t[`pa.roleName_${a.payload.role}`] : '');
  const what = (a: Approval) =>
    a.kind === 'widen_role'
      ? t['pa.apWiden'](a.subject_name, roleName(a))
      : a.kind === 'appoint_primary'
        ? t['ap.what'](a.subject_name)
        : t['pa.apErase'](a.subject_name);
  // Erasures and primary-head appointments need a second platform admin.
  const dual = (a: Approval) => a.kind === 'erase_person' || a.kind === 'appoint_primary';

  return (
    <>
      <p className="page-intro">{t['pa.approvalsIntro']}</p>
      <section className="panel p5" aria-labelledby={`${id}-t`}>
        <h2 className="panel-title" id={`${id}-t`}>
          {t['pa.waiting']}
        </h2>
        {error ? (
          <p className="error-text" role="alert">
            {error}
          </p>
        ) : !rows ? (
          <p role="status" className="text-muted">
            {t.loading}
          </p>
        ) : rows.length === 0 ? (
          <p className="empty-inline">
            <Pill kind="good">{t['overview.allClear']}</Pill> {t['pa.noApprovals']}
          </p>
        ) : (
          <ul className="approval-list">
            {rows.map((a) => (
              <li key={a.id} className="approval">
                <p className="approval-what">
                  {what(a)}{' '}
                  {a.subject_is_test && <span className="test-badge">{t['ctx.testBadge']}</span>}
                </p>
                <p className="text-muted">
                  <Link to={`/admin/schools/${a.school_id}`}>{a.school_name}</Link> · {a.school_code} ·{' '}
                  {t['pa.requestedBy'](a.requested_by_name ?? '–', fmtDate(locale, a.created_at))} ·{' '}
                  {t['pa.expiresOn'](fmtDate(locale, a.expires_at))}
                </p>
                {a.kind === 'widen_role' && <p className="form-hint">{t['pa.apWidenWhy']}</p>}
                {a.kind === 'erase_person' && a.payload.note && (
                  <p className="form-hint">{t['pa.apInstruction'](a.payload.note)}</p>
                )}
                {a.kind === 'appoint_primary' && (
                  <>
                    <p className="form-hint">
                      {t['ap.instruction'](a.payload.instruction_from ?? '–', fmtDate(locale, a.payload.instruction_on))}
                      {a.payload.note ? ` · ${a.payload.note}` : ''}
                    </p>
                    <p className="form-hint">{a.current_primary ? t['ap.replaces'](a.current_primary) : t['ap.noPrimary']}</p>
                  </>
                )}
                {!inSchool(a) && !(a.requested_by_me && dual(a)) && <p className="form-hint">{t['pa.apNeedsSupport']}</p>}
                {a.requested_by_me && dual(a) ? (
                  <p className="notice">{t['pa.apWaitingSecond']}</p>
                ) : (
                  <div className="form-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => start(a, false)}>
                      {t['pa.reject']}
                    </button>
                    <button
                      type="button"
                      className={`btn ${a.kind === 'erase_person' ? 'btn-danger' : 'btn-primary'}`}
                      onClick={() => start(a, true)}
                    >
                      {a.kind === 'erase_person' ? t['pa.approveErase'] : a.kind === 'appoint_primary' ? t['ap.confirm'] : t['pa.approve']}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {enterFor && (
        <EnterSupportDialog
          open
          schoolId={enterFor.a.school_id}
          schoolName={enterFor.a.school_name}
          defaultReason={enterFor.a.kind === 'widen_role' ? 'approval_no_primary_head' : 'account_recovery'}
          onClose={() => setEnterFor(null)}
          onEntered={async () => {
            const next = enterFor;
            setEnterFor(null);
            await onChange();
            setDecide(next);
          }}
        />
      )}

      <Dialog
        open={!!decide}
        title={decide?.approve ? t['pa.approveTitle'] : t['pa.rejectTitle']}
        intro={
          decide && (
            <p className="form-hint">
              {what(decide.a)} · {decide.a.school_name}
            </p>
          )
        }
        submitLabel={
          decide?.approve
            ? decide.a.kind === 'erase_person'
              ? t['pa.approveErase']
              : decide.a.kind === 'appoint_primary'
                ? t['ap.confirm']
                : t['pa.approve']
            : t['pa.reject']
        }
        danger={!!decide?.approve && decide.a.kind === 'erase_person'}
        busy={busy}
        error={dError}
        onSubmit={submit}
        onClose={() => setDecide(null)}
      >
        {decide?.approve && decide.a.kind === 'widen_role' && (
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-cw`}>
              {t['pa.confirmedWith']}
            </label>
            <input
              id={`${id}-cw`}
              className="field"
              value={confirmed}
              onChange={(e) => setConfirmed(e.target.value)}
              placeholder={decide.a.signatory ?? undefined}
              aria-describedby={`${id}-cwh`}
            />
            <p className="form-hint" id={`${id}-cwh`}>
              {t['pa.confirmedWithHint'](decide.a.signatory ?? '–')}
            </p>
          </div>
        )}
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-n`}>
            {t['pa.noteOptional']}
          </label>
          <input id={`${id}-n`} className="field" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Dialog>
    </>
  );
}

// ── Platform log ───────────────────────────────────────────────────────────

export function AdminLog() {
  const { t } = useI18n();
  return (
    <AdminFrame section="log" title={t['pa.navLog']}>
      {() => <LogBody />}
    </AdminFrame>
  );
}

function LogBody() {
  const { t } = useI18n();
  const [scope, setScope] = useState<'platform' | 'all' | string>('platform');
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  useEffect(() => {
    rpc<SchoolRow[]>('platform_list_schools').then((r) => r.data && setSchools(r.data));
  }, []);
  const isSchool = scope !== 'platform' && scope !== 'all';
  return (
    <>
      <p className="page-intro">{t['pa.logIntro']}</p>
      <div className="filters">
        <span className="select-wrap">
          <select className="select" aria-label={t['pa.logScope']} value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="platform">{t['pa.logPlatform']}</option>
            <option value="all">{t['pa.logAll']}</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        </span>
      </div>
      <AuditList
        key={scope}
        scope={isSchool ? 'school' : (scope as 'platform' | 'all')}
        schoolId={isSchool ? scope : null}
        title={isSchool ? t['pa.logSchool'](schools.find((s) => s.id === scope)?.name ?? '') : scope === 'all' ? t['pa.logAll'] : t['pa.logPlatform']}
        showSchool={!isSchool}
      />
    </>
  );
}

// ── Critical-records archive ───────────────────────────────────────────────

const KINDS = [
  'school_created',
  'school_type_changed',
  'school_state_changed',
  'paperwork_changed',
  'access_widened',
  'primary_head_changed',
  'erasure_receipt',
  'suspension_notice',
  'offboarding_acknowledged',
  'offboarding_report',
] as const;

export function AdminArchive() {
  const { t } = useI18n();
  return (
    <AdminFrame section="archive" title={t['pa.navArchive']}>
      {() => <ArchiveBody />}
    </AdminFrame>
  );
}

function ArchiveBody() {
  const { t, locale } = useI18n();
  const id = useId();
  const [params, setParams] = useSearchParams();
  const kind = params.get('kind') ?? '';
  const school = params.get('school') ?? '';
  const [rows, setRows] = useState<ArchiveRow[] | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [schools, setSchools] = useState<SchoolRow[]>([]);

  useEffect(() => {
    rpc<SchoolRow[]>('platform_list_schools').then((r) => r.data && setSchools(r.data));
  }, []);

  const load = useCallback(
    async (before: number | null) => {
      const r = await rpc<{ rows: ArchiveRow[]; next_before: number | null }>('platform_archive_list', {
        p_kind: kind || null,
        p_school_id: school || null,
        p_before_id: before,
        p_limit: 50,
      });
      if (r.error) return setError(errorText(t, r.error));
      setRows((prev) => (before && prev ? [...prev, ...r.data.rows] : r.data.rows));
      setNext(r.data.next_before);
    },
    [kind, school, t],
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  return (
    <>
      <p className="page-intro">{t['pa.archiveIntro']}</p>
      <section className="panel p5" aria-labelledby={`${id}-t`}>
        <div className="panel-head wrap">
          <h2 className="panel-title" id={`${id}-t`}>
            {t['pa.records']}
          </h2>
          <div className="filters">
            <span className="select-wrap">
              <select className="select" aria-label={t['pa.filterKind']} value={kind} onChange={(e) => set('kind', e.target.value)}>
                <option value="">{t['pa.allKinds']}</option>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t[`pa.ak_${k}`]}
                  </option>
                ))}
              </select>
            </span>
            <span className="select-wrap">
              <select className="select" aria-label={t['pa.filterSchool']} value={school} onChange={(e) => set('school', e.target.value)}>
                <option value="">{t['admin.allSchools']}</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </span>
          </div>
        </div>
        {error ? (
          <p className="error-text" role="alert">
            {error}
          </p>
        ) : !rows ? (
          <p role="status" className="text-muted">
            {t.loading}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-muted">{t['pa.noRecords']}</p>
        ) : (
          <ul className="archive-list">
            {rows.map((r) => (
              <li key={r.id}>
                <div className="archive-row">
                  <span className="log-time">{fmtDateTime(locale, r.created_at)}</span>
                  <span>
                    <strong>{t[`pa.ak_${r.kind as (typeof KINDS)[number]}`] ?? r.kind}</strong>
                    {r.school_name && (
                      <>
                        {' '}
                        · {r.school_name} <span className="code-chip">{r.school_code}</span>
                      </>
                    )}
                  </span>
                  <span className="text-muted">{r.created_by}</span>
                  <span className="pill-row">
                    <button
                      type="button"
                      className="link-btn"
                      aria-expanded={openId === r.id}
                      onClick={() => setOpenId(openId === r.id ? null : r.id)}
                    >
                      {openId === r.id ? t['pa.hide'] : t['pa.view']}
                    </button>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() =>
                        download(
                          `swipe-${r.kind}-${r.school_code ?? 'platform'}-${r.created_at.slice(0, 10)}.json`,
                          JSON.stringify({ id: r.id, kind: r.kind, school: r.school_name, school_code: r.school_code, created_at: r.created_at, created_by: r.created_by, content: r.content }, null, 2),
                          'application/json',
                        )
                      }
                    >
                      {t['pa.download']}
                    </button>
                  </span>
                </div>
                {openId === r.id && <RecordView content={r.content} />}
              </li>
            ))}
          </ul>
        )}
        {next && (
          <button type="button" className="btn btn-ghost" onClick={() => load(next)}>
            {t['pa.loadMore']}
          </button>
        )}
      </section>
    </>
  );
}

function RecordView({ content }: { content: unknown }) {
  if (content === null || typeof content !== 'object') return <span>{String(content ?? '–')}</span>;
  if (Array.isArray(content)) {
    return (
      <ul className="bullets">
        {content.map((v, i) => (
          <li key={i}>
            <RecordView content={v} />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <dl className="kv record">
      {Object.entries(content as Record<string, unknown>).map(([k, v]) => (
        <div key={k}>
          <dt>{k.replace(/_/g, ' ')}</dt>
          <dd>
            <RecordView content={v} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ── Platform settings ──────────────────────────────────────────────────────

export function AdminSettings() {
  const { t } = useI18n();
  return (
    <AdminFrame section="settings" title={t['pa.navSettings']}>
      {() => <SettingsBody />}
    </AdminFrame>
  );
}

function SettingsBody() {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [s, setS] = useState<PlatformSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mfaOpen, setMfaOpen] = useState<null | { user_id: string; name: string }>(null);
  const [how, setHow] = useState('');
  const [sweepOpen, setSweepOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await rpc<PlatformSettings>('platform_settings_get');
    if (r.error) return setError(errorText(t, r.error));
    setS(r.data);
    setEmail(r.data.notification_email ?? '');
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(fn: string, args: Record<string, unknown>, msg: string) {
    setBusy(true);
    setSaveError(null);
    const r = await rpc<unknown>(fn, args);
    setBusy(false);
    if (r.error) {
      setSaveError(errorText(t, r.error));
      return false;
    }
    announce(msg);
    await load();
    return true;
  }

  if (error) {
    return (
      <p className="error-text" role="alert">
        {error}
      </p>
    );
  }
  if (!s) return <p role="status" className="text-muted">{t.loading}</p>;
  const h = s.health;
  const freeLow = h.free_bytes < 200_000_000;
  const others = s.admins.filter((a) => !a.is_me);
  const allMfa = s.admins.every((a) => a.mfa_factors > 0);

  return (
    <>
      {saveError && (
        <p className="error-text" role="alert">
          {saveError}
        </p>
      )}
      <FeatureSwitchesPanel />
      <div className="two-col two-col-even">
        <section className="panel p5" aria-labelledby={`${id}-ne`}>
          <h2 className="panel-title" id={`${id}-ne`}>
            {t['pa.notifEmail']}
          </h2>
          <p className="form-hint">{s.email_enabled ? t['pa.notifEmailOn'] : t['pa.notifEmailOff']}</p>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              act('platform_set_notification_email', { p_email: email || null }, t['pa.saved']);
            }}
            noValidate
          >
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-em`}>
                {t['pa.email']}
              </label>
              <input id={`${id}-em`} className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {t['pa.save']}
              </button>
            </div>
          </form>
        </section>

        <section className="panel p5" aria-labelledby={`${id}-mf`}>
          <h2 className="panel-title" id={`${id}-mf`}>
            {t['pa.mfaEnforce']}
          </h2>
          <p>
            {s.require_mfa ? <Pill kind="good">{t['pa.on']}</Pill> : <Pill kind="warn">{t['pa.off']}</Pill>}{' '}
            {s.require_mfa ? t['pa.mfaOnBody'] : t['pa.mfaOffBody']}
          </p>
          {!s.require_mfa && (
            <ul className="bullets">
              <li className={s.admins.length >= 2 ? 'ok' : ''}>{t['pa.mfaReqTwo'](s.admins.length)}</li>
              <li className={allMfa ? 'ok' : ''}>{t['pa.mfaReqAll']}</li>
              <li className={s.my_session_aal === 'aal2' ? 'ok' : ''}>
                {t['pa.mfaReqSession']} {s.my_session_aal !== 'aal2' && <Link to="/mfa">{t['pa.mfaVerifyNow']}</Link>}
              </li>
            </ul>
          )}
          <div className="form-actions">
            <button
              type="button"
              className={s.require_mfa ? 'btn btn-ghost' : 'btn btn-primary'}
              disabled={busy}
              onClick={() => act('platform_set_require_mfa', { p_on: !s.require_mfa }, t['pa.saved'])}
            >
              {s.require_mfa ? t['pa.mfaTurnOff'] : t['pa.mfaTurnOn']}
            </button>
          </div>
        </section>
      </div>

      <section className="panel p5" aria-labelledby={`${id}-ad`}>
        <h2 className="panel-title" id={`${id}-ad`}>
          {t['pa.platformAdmins']}
        </h2>
        <p className="form-hint">{t['pa.platformAdminsHint']}</p>
        <div className="table-wrap" tabIndex={0} role="region" aria-labelledby={`${id}-ad`}>
          <table className="table">
            <caption className="visually-hidden">{t['pa.platformAdmins']}</caption>
            <thead>
              <tr>
                <th scope="col">{t['admin.colName']}</th>
                <th scope="col">{t['admin.colEmail']}</th>
                <th scope="col">{t['pa.authenticators']}</th>
                <th scope="col">{t['pa.lastSignInLabel']}</th>
                <th scope="col">
                  <span className="visually-hidden">{t['pa.actions']}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {s.admins.map((a) => (
                <tr key={a.user_id}>
                  <td>
                    {a.name}
                    {a.is_me && <span className="text-muted"> · {t['pa.you']}</span>}
                  </td>
                  <td className="muted">{a.email}</td>
                  <td>{a.mfa_factors >= 2 ? a.mfa_factors : <Pill kind="warn">{a.mfa_factors}</Pill>}</td>
                  <td className="muted">{a.last_sign_in_at ? fmtDateTime(locale, a.last_sign_in_at) : t['pa.never']}</td>
                  <td>
                    {!a.is_me && a.mfa_factors > 0 && (
                      <button type="button" className="link-btn" onClick={() => setMfaOpen({ user_id: a.user_id, name: a.name })} aria-label={t['pa.resetMfaFor'](a.name)}>
                        {t['pa.resetMfa']}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {others.length === 0 && <p className="notice">{t['pa.attnOneAdmin']}</p>}
      </section>

      <div className="two-col two-col-even">
        <section className="panel p5" aria-labelledby={`${id}-hs`}>
          <h2 className="panel-title" id={`${id}-hs`}>
            {t['pa.health']}
          </h2>
          <dl className="kv">
            <div>
              <dt>{t['pa.dbSize']}</dt>
              <dd>
                {fmtBytes(h.db_size_bytes)} / {fmtBytes(h.db_limit_bytes)}
              </dd>
            </div>
            <div>
              <dt>{t['pa.freeSpace']}</dt>
              <dd>
                {fmtBytes(h.free_bytes)} {freeLow && <Pill kind="crit">{t['pa.actionNeeded']}</Pill>}
              </dd>
            </div>
            <div>
              <dt>{t['pa.logSize']}</dt>
              <dd>
                {fmtBytes(h.audit_log_bytes)} / {fmtBytes(h.audit_log_cap_bytes)}
              </dd>
            </div>
            <div>
              <dt>{t['pa.archiveRows']}</dt>
              <dd>{h.records_archive_rows}</dd>
            </div>
          </dl>
          <p className="form-hint">{t['pa.healthHint']}</p>
        </section>

        <section className="panel p5" aria-labelledby={`${id}-mt`}>
          <h2 className="panel-title" id={`${id}-mt`}>
            {t['pa.maintenance']}
          </h2>
          <p className="form-hint">{t['pa.sweepHint']}</p>
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setSweepOpen(true)}>
              {t['pa.sweepNow']}
            </button>
          </div>
        </section>
      </div>

      <Dialog
        open={!!mfaOpen}
        title={t['pa.resetMfaFor'](mfaOpen?.name ?? '')}
        intro={<p className="form-hint">{t['pa.resetMfaAdminIntro']}</p>}
        submitLabel={t['pa.resetMfa']}
        danger
        busy={busy}
        onClose={() => setMfaOpen(null)}
        onSubmit={async () => {
          if (mfaOpen && (await act('platform_reset_mfa', { p_user_id: mfaOpen.user_id, p_school_id: null, p_confirmed_how: how }, t['pa.saved']))) {
            setMfaOpen(null);
            setHow('');
          }
        }}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-how`}>
            {t['pa.confirmedHow']}
          </label>
          <input id={`${id}-how`} className="field" value={how} onChange={(e) => setHow(e.target.value)} />
        </div>
      </Dialog>

      <Dialog
        open={sweepOpen}
        title={t['pa.sweepNow']}
        intro={<p className="form-hint">{t['pa.sweepIntro']}</p>}
        submitLabel={t['pa.sweepGo']}
        danger
        busy={busy}
        onClose={() => setSweepOpen(false)}
        onSubmit={async () => {
          setBusy(true);
          const r = await rpc<number>('platform_erase_expired_tests');
          setBusy(false);
          if (r.error) return setSaveError(errorText(t, r.error));
          announce(t['pa.sweepDone'](r.data));
          setSweepOpen(false);
          await load();
        }}
      />
    </>
  );
}
